/**
 * 动作文法 —— 两个可连接点凑到一起，形成的是**哪一种关系**、谁是 from 谁是 to。
 * 对应技术设计 §5.4（Relation 类型）与 §5.1（装配图查询的方向约定）。
 *
 * 这一层必须完全泛化：只认端口/槽位的 `kind`，**不认任何具体实验或器材 id**
 * （v1.4 §12.3.2 B2 门禁）。新增实验只要复用已有 kind 就不用改这张表；
 * 真需要新 kind 时，加的是通用原语，不是特例分支（§22 红线 12）。
 */

import type { AnchorFacet } from "../eom/port.js";
import type { RelationType } from "../eom/relation.js";

export interface BondRule {
  /** 主动侧（direction "out"）的 kind */
  from: string;
  /** 接收侧（direction "in"）的 kind */
  to: string;
  relation: RelationType;
  /** 建立后要挂的约束类型，供 constraint-solver 消费（§5.5） */
  constraint: ConstraintKind;
}

/**
 * 连接后的跟随约束（§5.5「连接后器材要跟得上」）。
 *   followPort  —— 接收方整体跟随主动方端口（塞子跟着试管口走）
 *   anchorEnd   —— 只把一端锚到对方端口，本体不整体移动（导管两端各自锚定，中段重绘）
 *   poseOverride—— 接收方姿态被主动方强制校正（试管被夹住 → 校正为略向下倾斜）
 *   contain     —— 接收方被纳入主动方内部，不再独立参与布局（试剂进试管）
 */
export type ConstraintKind = "followPort" | "anchorEnd" | "poseOverride" | "contain";

/**
 * 方向约定来自 §5.1 的五条查询，逐条核对过：
 *   hasRelation(TestTube.mouth,   Cork,           "insert")   mouth  是 out，sealRing 是 in
 *   hasRelation(Cork.tubeHole,    RubberTube.input,"connect")  tubeHole 是 out，input 是 in
 *   hasRelation(RubberTube.output,GasBottle.mouth,"collect")   output 是 out，mouth  是 in
 *   hasRelation(IronStand.clamp,  TestTube.body,  "clamp")     clamp  是 out，body   是 both
 *   hasRelation(AlcoholLamp.flame,TestTube.bottom,"heat")      flame  是 out，bottom 是 in
 * 即 **from = direction "out" 的一侧，to = "in" 的一侧**，"both" 两边都能当。
 */
const BOND_TABLE: readonly BondRule[] = [
  { from: "opening", to: "opening", relation: "insert", constraint: "followPort" },
  { from: "gas_out", to: "tube_end", relation: "connect", constraint: "anchorEnd" },
  { from: "tube_end", to: "opening", relation: "collect", constraint: "anchorEnd" },
  { from: "clamp", to: "clamp", relation: "clamp", constraint: "poseOverride" },
  { from: "flame", to: "heat_target", relation: "heat", constraint: "followPort" },
  { from: "opening", to: "reagent_container", relation: "pour", constraint: "contain" },
  // 固体消耗品放进容器：棉花、木条、滤纸、金属片都走这条，不是「倾倒」
  { from: "solid_item", to: "reagent_container", relation: "place", constraint: "contain" },
  { from: "tube_end", to: "water_area", relation: "place", constraint: "anchorEnd" },
] as const;

/** 整件器材落进槽位（拖集气瓶进水槽收集区）——没有端口参与，恒为 place。 */
const OBJECT_INTO_SLOT: { relation: RelationType; constraint: ConstraintKind } = {
  relation: "place",
  constraint: "followPort",
};

export interface Bond {
  from: AnchorFacet;
  to: AnchorFacet;
  relation: RelationType;
  constraint: ConstraintKind;
}

function canBeSource(facet: AnchorFacet): boolean {
  return facet.anchorType === "port" && (facet.direction === "out" || facet.direction === "both");
}

function canBeTarget(facet: AnchorFacet): boolean {
  return facet.direction === "in" || facet.direction === "both" || facet.anchorType !== "port";
}

function lookup(fromKind: string, toKind: string): BondRule | undefined {
  return BOND_TABLE.find((rule) => rule.from === fromKind && rule.to === toKind);
}

/**
 * 定出两个可连接点之间的关系与方向。**不做兼容性判断** ——
 * 兼容性是 `anchorsCompatible`（语义标签）的职责，两者都必须通过才成候选。
 * 返回 null 表示这两个点即使兼容也不构成任何已知动作，不建立关系。
 */
export function resolveBond(a: AnchorFacet, b: AnchorFacet): Bond | null {
  for (const [source, target] of [
    [a, b],
    [b, a],
  ] as const) {
    if (target.anchorType === "object") continue; // 整件器材只能当接收方的「被放入」，不能当槽位

    if (source.anchorType === "object" && target.anchorType === "slot") {
      return { from: source, to: target, ...OBJECT_INTO_SLOT };
    }

    if (!canBeSource(source) || !canBeTarget(target)) continue;
    if (source.kind === null || target.kind === null) continue;

    const rule = lookup(source.kind, target.kind);
    if (rule) {
      return { from: source, to: target, relation: rule.relation, constraint: rule.constraint };
    }
  }
  return null;
}

/** 契约自检与调试用：当前支持的动作总数。 */
export function listBondRules(): readonly BondRule[] {
  return BOND_TABLE;
}
